import { useState } from 'react'
import type { ListField, ListFieldConfig } from '@ztechapps/common.hook'
import { useDeepCompareEffect } from '@ztechapps/common.hook'
import sortBy from 'lodash/sortBy'
import merge from 'lodash/merge'
import forEach from 'lodash/forEach'
import mapValues from 'lodash/mapValues'
import pick from 'lodash/pick'
import keys from 'lodash/keys'
import type { LocalStorage } from '@ztechapps/common.utils'
import { humanize } from '@ztechapps/common.utils'
import map from 'lodash/map'
import type { TableFields } from '../types'

export type UseConfigStorageResult = {
  listFields: ListField[]
  setFields: (value: TableFields) => void
  fields: TableFields
  onResetConfig: () => Promise<void>
}
export const useTableConfig = (
  storageName: string,
  fields: ListFieldConfig,
  localStorage: LocalStorage
): UseConfigStorageResult => {
  const [listFields, setListFields] = useState<ListField[]>(null)
  const [_fields, _setFields] = useState(fields)

  //load from the local storage initially
  useDeepCompareEffect(() => {
    const storedValue = localStorage.get(storageName, true)

    // A field's own definition — type, fieldSettings, exclude*, … — always comes from the live
    // `fields` prop, never from local storage. Only the user's own layout choices are persisted
    // and merged back in — visibility, order and width, the sole properties `setFields` ever
    // persists (see `setFields` below). Preferences for a column no longer in `fields` (removed
    // since the snapshot was saved) are dropped — otherwise they'd survive as a nameless entry.
    const preferences = pick(
      mapValues(storedValue, (m) => pick(m, ['isVisible', 'index', 'size'])),
      keys(fields)
    )
    setFields(merge({}, fields, preferences))
  }, [localStorage, fields])

  const setFields = (value: TableFields) => {
    //only the user's layout preferences are ever persisted — never a field's own definition
    const storedValue = localStorage.get(storageName, true)
    const storedPreferences = mapValues(storedValue, (m) => pick(m, ['isVisible', 'index', 'size']))
    const incomingPreferences = mapValues(value, (m) => pick(m, ['isVisible', 'index', 'size']))
    // Drop preferences for a column no longer in `fields` (removed since the snapshot was saved)
    // — otherwise they'd survive as a nameless entry in the displayed column list.
    const newPreferences = pick(merge({}, storedPreferences, incomingPreferences), keys(fields))

    //a field's own definition always comes from the live `fields` prop; only the preferences
    //above are ever merged on top of it, so a live value (e.g. an async `fieldSettings.listValues`)
    //can never be shadowed by a stale local storage snapshot
    const newValue = merge({}, fields, newPreferences)

    //we make sure it has the default
    //we need to fill up also the default display storageName, key, and isVisible, size from the object key

    forEach(newValue, (m, fieldName) => {
      if (!fieldName) return

      m.displayName = m?.displayName ?? humanize(m?.name)
      m.isVisible = m?.isVisible ?? true

      m.key = m?.key ?? fieldName

      let size = m?.size ?? 100
      if (size < 100) size = 100
      m.size = size
    })

    _setFields(newValue)
    //we store only the user's preferences on localStorage — never the field's own definition
    localStorage.set(storageName, newPreferences)
  }

  useDeepCompareEffect(() => {
    //convert to array of fields
    let _listFields = map(_fields, (m) => m)

    //sort it by index
    _listFields = sortBy(_listFields, (m) => m.index)

    //same index values will be shown immediately
    _listFields = map(_listFields, (m, index) => ({
      ...m,
      index: m?.index ?? index,
    }))

    //sort again by index for the last time
    setListFields(sortBy(_listFields, (m) => m.index))
  }, [_fields])

  const onResetConfig = async () => {
    localStorage.remove(storageName)
    //we need to set the fields to the default, this will trigger the effect
    setFields(fields)
  }

  return {
    fields: _fields,
    setFields,
    listFields,
    onResetConfig,
  }
}
